import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ScheduleService } from '../schedule.service';
import { MessagerComponent } from '../../messager/messager/messager.component';
import { AuthService } from '../../auth.service';
import * as moment from 'moment-timezone';

@Component({
  template: `
    <h2 mat-dialog-title style="color: #424242;">Edit Clock-in and Clock-out Times</h2>
    <div mat-dialog-content>
      <!-- Clock-in Time Section -->
      <div style="margin-bottom: 25px;">
        <h3 style="margin: 0 0 10px 0; color: #8bc34a; font-size: 16px;font-weight: 600;">Clock-in Time</h3>
        <div style="margin-bottom: 10px;">
          <label style="display: block; margin-bottom: 5px; font-weight: 500;">Current Time:
            <span style="color: #666; margin: 0;">{{data.clockInDisplay}}</span>
          </label>
        </div>
        <div>
          <label style="display: block; margin-bottom: 5px; font-weight: 500;">New Time:</label>
          <input type="time"
                 [(ngModel)]="clockInTime"
                 [disabled]="isLoading"
                 style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
        </div>
      </div>

      <!-- Clock-out Time Section -->
      <div style="margin-bottom: 25px;">
        <h3 style="margin: 0 0 10px 0; color: #8bc34a; font-size: 16px;font-weight: 600;">Clock-out Time</h3>
        <div style="margin-bottom: 10px;">
          <label style="display: block; margin-bottom: 5px; font-weight: 500;">Current Time:
            <span style="color: #666; margin: 0;">{{data.clockOutDisplay}}</span>
          </label>
        </div>
        <div>
          <label style="display: block; margin-bottom: 5px; font-weight: 500;">New Time:</label>
          <input type="time"
                 [(ngModel)]="clockOutTime"
                 [disabled]="isLoading"
                 style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
        </div>
      </div>

      <div style="margin-bottom: 20px;">
        <label style="display: block; margin-bottom: 5px; font-weight: 500;">Reason for Change: <span style="color: red;">*</span></label>
        <textarea [(ngModel)]="reason"
                  [disabled]="isLoading"
                  placeholder="Please provide a reason for this time change..."
                  style="width: 100%; height: 80px; padding: 8px; border: 1px solid #ddd; border-radius: 4px; resize: vertical;"
                  required></textarea>
      </div>
    </div>
    <div *ngIf="showDayStart" style="margin-bottom: 15px; color: red; font-weight: 600;">
      Daystart: {{ day.daystart | date:'short' }} - Dayend: {{ day.dayend | date:'short' }}
    </div>

    <div mat-dialog-actions style="justify-content: flex-end; gap: 10px;">
      <button mat-button (click)="onCancel()" [disabled]="isLoading" style="color: #666;">Cancel</button>
      <button mat-button (click)="onSave()"
              [disabled]="!reason || isLoading"
              style="background-color: #8bc34a; color: white; position: relative;">
        <span *ngIf="!isLoading">Save Changes</span>
        <span *ngIf="isLoading" style="display: flex; align-items: center; gap: 8px;">
          <mat-spinner [diameter]="20" style="display: inline-block;"></mat-spinner>
          Saving...
        </span>
      </button>
    </div>
  `
})
export class EditTimeModalComponent {
  clockInTime: string;
  clockOutTime: string;
  reason: string = '';
  isLoading: boolean = false;
  scheduleService: ScheduleService;
  messager: MessagerComponent;
  day: any;
  calculateJobTotalTime: (startTime: string, endTime: string) => any;
  showDayStart: boolean = false;
  originalData : any;

  constructor(
    public dialogRef: MatDialogRef<EditTimeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.clockInTime = this.formatTimeForInput(data.clockInTimeForInput);
    this.clockOutTime = this.formatTimeForInput(data.clockOutTimeForInput);
    this.scheduleService = data.scheduleService;
    this.messager = data.messager;
    this.day = data.day;
    this.calculateJobTotalTime = data.calculateJobTotalTime;
  }

  ngOnInit(): void {
    console.log(this.data);
    this.originalData = {
      starttime : this.data.job.starttime,
      endtime: this.data.job.endtime,
      totaltime : this.data.job.totaltime
    }
  }

  formatTimeForInput(timeString: string): string {
    if (!timeString) return '';

    const ampmMatch = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = ampmMatch[2];
      const ampm = ampmMatch[4].toUpperCase();

      if (ampm === 'PM' && hours < 12) {
        hours += 12;
      }
      if (ampm === 'AM' && hours === 12) {
        hours = 0;
      }

      const h24 = String(hours).padStart(2, '0');
      return `${h24}:${minutes}`;
    }

    const timeMatch = timeString.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }

    const simpleTimeMatch = timeString.match(/(\d{2}):(\d{2})/);
    if (simpleTimeMatch) {
      return `${simpleTimeMatch[1]}:${simpleTimeMatch[2]}`;
    }

    const date = new Date(timeString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;

  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.reason || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.messager.openSnackBar('Saving time changes...', '', 'successAlert');

    let result: any = {
      reason: this.reason
    };

    if (this.clockInTime) {
      result.clockInTime = this.convertEasternTimeToUTC(this.clockInTime, this.data.clockInTime);
    }

    if (this.clockOutTime) {
      result.clockOutTime = this.convertEasternTimeToUTC(this.clockOutTime, this.data.clockOutTime);
    }

    let totaltime = null;
    if (result.clockInTime && result.clockOutTime) {

      totaltime = this.calculateJobTotalTime(result.clockInTime, result.clockOutTime);
      //make sure total job time is not negative
      if (totaltime.isNegative){
        this.messager.openSnackBar('Error: Start time cannot be AFTER end time.', '', 'errorAlert');
        this.isLoading = false;
        return;
      }

      //make sure start time and end time are not before the day start or after day end.
      const dayStartUTC = new Date(this.day.daystart).toISOString();
      //replace the seconds in this iso string with 59
      const dayStartUTCWithSeconds = dayStartUTC.substring(0, 17) + "59" + dayStartUTC.substring(19);

      const dayEndUTC = new Date(this.day.dayend).toISOString();
      this.showDayStart = true;

      if (new Date(result.clockInTime) < new Date(dayStartUTCWithSeconds) || new Date(result.clockOutTime) > new Date(dayEndUTC)) {
        this.messager.openSnackBar('Error: Clock-in and Clock-out times must be within the scheduled day range.', '', 'errorAlert');
        this.isLoading = false;
        return;
      }

    }

    console.log(this.originalData);
    const payload = {
      job_id: this.data.job.job_id,
      starttime: result.clockInTime || this.data.clockInTime,
      endtime: result.clockOutTime || this.data.clockOutTime,
      timezone: this.day.timezone || "America/New_York",
      reason: result.reason,
      street_address: this.data.job.street_address,
      subdivision_name: this.data.job.subdivision_name,
      lot: this.data.job.lot,
      customer_jobtype: this.data.job.customer_jobtype,
      totaltime: totaltime,
      employee_id: this.day.employee_id,
      day: this.day.day,
      daystart: this.day.daystart,
      dayend: this.day.dayend,
      who: this.data.who,
      original_data : this.originalData
    };

    console.log(payload);
    this.scheduleService.updateJobTimes(payload).subscribe(
      (response) => {
        this.isLoading = false;
        this.messager.openSnackBar('Time changes saved successfully!', '', 'successAlert');
        this.dialogRef.close(result);
      },
      (error) => {
        this.isLoading = false;
        const errorMessage = error?.error?.Message || error?.message || error?.error?.message || 'Unknown error';
        this.messager.openSnackBar('Error saving time changes: ' + errorMessage, '', 'errorAlert');
      }
    );
  }

  convertEasternTimeToUTC(easternTime: string, originalISO: string): string {
    const [hours, minutes] = easternTime.split(':');

    const originalDate = new Date(originalISO);

    function isInDST(date) {
      const marchTransition = new Date(Date.UTC(date.getUTCFullYear(), 2, 14));
      const novemberTransition = new Date(Date.UTC(date.getUTCFullYear(), 10, 7));
      marchTransition.setUTCDate(14 - (marchTransition.getUTCDay() || 7) + 7);
      novemberTransition.setUTCDate(7 - (novemberTransition.getUTCDay() || 7));

      return date >= marchTransition && date < novemberTransition;
    }

    let utcOffsetHours = -5;
    if (isInDST(originalDate)) {
      utcOffsetHours = -4;
    }

    const easternHours = parseInt(hours);
    const easternMinutes = parseInt(minutes);

    let utcHours = easternHours - utcOffsetHours;
    let utcMinutes = easternMinutes;

    let utcDate = new Date(originalDate);
    if (utcHours >= 24) {
      utcHours -= 24;
      utcDate.setUTCDate(utcDate.getUTCDate() + 1);
    } else if (utcHours < 0) {
      utcHours += 24;
      utcDate.setUTCDate(utcDate.getUTCDate() - 1);
    }

    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getUTCDate()).padStart(2, '0');
    const hour = String(utcHours).padStart(2, '0');
    const minute = String(utcMinutes).padStart(2, '0');

    return `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
  }
}


@Component({
  template: `
    <h1  *ngIf='version == 1' mat-dialog-title style="text-align:center;">Start Day</h1>
    <h1  *ngIf='version == 2' mat-dialog-title style="text-align:center;">End Day</h1>
    <div style="display: flex;
    justify-content: center;">
      <p *ngIf='version == 1' style="text-align: center; font-weight:200;">Click 'Confirm' to begin your timecard.</p>
      <p *ngIf='version == 2' style="text-align: center; font-weight:200;">Click 'Confirm' to end your day.</p>
    </div>
    <div style="display: flex;
    justify-content: center;">
      <button mat-button (click)="dref.close(1)"  style="border: 0;
    height: 40px;
    width: 100px;
    margin: 10px;">Confirm</button>
      <button mat-button (click)="dref.close(2)"  style="background: #ffffffaf;
    border: 0;
    height: 40px;
    margin: 10px;
    width: 100px;">Cancel</button>
    </div>
  `
})
export class StartDayWarningDialogComponent {

  constructor(public dref: MatDialogRef<StartDayWarningDialogComponent>,  @Inject(MAT_DIALOG_DATA) public data:any){}
  version = this.data.version;

}


@Component({
  template: `
    <h1 mat-dialog-title style="text-align:center;">End Day Warning</h1>
    <div mat-dialog-content>
      <p style="margin-bottom:12px;">You are attempting to end your day, however, the following jobs are still status <strong>"Scheduled"</strong>. Would you like to proceed with ending your day?</p>
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead>
          <tr style="background:#f5f5f5;">
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd;">Location</th>
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd;">Address</th>
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd;">Job Type</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let job of jobs">
            <td style="padding:5px 8px;border-bottom:1px solid #eee;">{{job.subdivision_name}} Lot {{job.lot}}</td>
            <td style="padding:5px 8px;border-bottom:1px solid #eee;">{{job.street_address}}</td>
            <td style="padding:5px 8px;border-bottom:1px solid #eee;">{{job.customer_jobtype}}</td>
          </tr>
        </tbody>
      </table>
      <p style="margin-top:14px;font-size:12px;color:#888;">Your supervisor will be notified if you end your day with unresolved jobs.</p>
    </div>
    <div style="display:flex;justify-content:center;gap:12px;padding:16px;">
      <button mat-raised-button color="warn" (click)="dref.close(1)">End Day Anyway</button>
      <button mat-button (click)="dref.close(2)">Cancel</button>
    </div>
  `
})
export class ScheduledJobsWarningDialogComponent {
  jobs: any[] = this.data.jobs || [];
  constructor(public dref: MatDialogRef<ScheduledJobsWarningDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any) {}
}


@Component({
  templateUrl: './timecard.component.html',
  styleUrls: ['./schedule.component.css']
})
export class TimeCardDialogComponent implements OnInit {

  title;
  arr = [];
  givenData;
  employeetype;
  canEditTimes: boolean = false;
  allNotes: any[] = [];
  generalNoteCount: number = 0;
  jobNoteCounts: { [jobId: number]: number } = {};

  constructor(public dref: MatDialogRef<TimeCardDialogComponent>, public scheduleService: ScheduleService, public dialog: MatDialog, private cdr: ChangeDetectorRef, public messager: MessagerComponent, public auth: AuthService, @Inject(MAT_DIALOG_DATA) public data:any){
    this.givenData = data;
    this.employeetype = data.employeetype;
    this.canEditTimes = data.canEditTimes || false;
   }

  ngOnInit(): void {
    this.loadNoteCounts();
  }

  loadNoteCounts(): void {
    if (!this.data.day || !this.data.day.did) return;
    this.scheduleService.getTimecardNotesByDay(this.data.day.did).subscribe(
      (res) => {
        this.allNotes = res.Data || [];
        this.generalNoteCount = this.allNotes.filter(n => n.job_id == null).length;
        this.jobNoteCounts = {};
        this.allNotes.forEach(n => {
          if (n.job_id) {
            this.jobNoteCounts[n.job_id] = (this.jobNoteCounts[n.job_id] || 0) + 1;
          }
        });
      },
      (error) => {
        console.error('Error loading note counts:', error);
      }
    );
  }

  getJobNoteCount(jobId: number): number {
    return this.jobNoteCounts[jobId] || 0;
  }

  openNoteDialog(job: any): void {
    const dialogRef = this.dialog.open(TimecardNoteDialogComponent, {
      width: '500px',
      data: {
        did: this.data.day.did,
        employee_id: this.data.day.employee_id,
        job_id: job ? job.job_id : null,
        job: job,
        who: this.data.who,
        loggedInEmployeeId: this.auth.getEmployeeId(),
        scheduleService: this.scheduleService,
        messager: this.messager
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadNoteCounts();
    });
  }

  openEditTimeModal(job: any): void {
    this.messager.openSnackBar('Loading edit times...', '', 'successAlert');
    const dialogRef = this.dialog.open(EditTimeModalComponent, {
      width: '600px',
      data: {
        who: this.data.who,
        job: job,
        clockInTime: job.starttime,
        clockOutTime: job.endtime,
        clockInDisplay: this.getTimeZoneDate(job.starttime, false),
        clockOutDisplay: this.getTimeZoneDate(job.endtime, false),
        clockInTimeForInput: this.getTimeZoneDate(job.starttime, false),
        clockOutTimeForInput: this.getTimeZoneDate(job.endtime, false),
        scheduleService: this.scheduleService,
        messager: this.messager,
        day: this.givenData.day,
        calculateJobTotalTime: (startTime: string, endTime: string) => this.calculateJobTotalTime(startTime, endTime)
      },
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const oldClockIn = job.starttime;
        const oldClockOut = job.endtime;

        if (result.clockInTime) {
          job.starttime = result.clockInTime;
        }
        if (result.clockOutTime) {
          job.endtime = result.clockOutTime;
        }

        if (result.clockInTime && result.clockOutTime) {
          job.totaltime = this.calculateJobTotalTime(result.clockInTime, result.clockOutTime);
        }


        setTimeout(() => {
          this.cdr.detectChanges();
        }, 100);

        if (!job.timeChangeReasons) {
          job.timeChangeReasons = [];
        }

        if (result.clockInTime && result.clockInTime !== oldClockIn) {
          job.timeChangeReasons.push({
            timeType: 'starttime',
            oldTime: oldClockIn,
            newTime: result.clockInTime,
            reason: result.reason,
            changedAt: new Date().toISOString()
          });
        }

        if (result.clockOutTime && result.clockOutTime !== oldClockOut) {
          job.timeChangeReasons.push({
            timeType: 'endtime',
            oldTime: oldClockOut,
            newTime: result.clockOutTime,
            reason: result.reason,
            changedAt: new Date().toISOString()
          });
        }
      }
    });
  }

  hasTimeChange(job: any, timeType: string): boolean {
    return job.timeChangeReasons && job.timeChangeReasons.some((change: any) => change.timeType === timeType);
  }

  isQIJob(job: any): boolean {
    return job.customer_jobtype.toLowerCase().includes('qi') ||
           job.customer_jobtype.toLowerCase().includes('draincam') ||
           job.customer_jobtype.toLowerCase().includes('framing') ||
           job.customer_jobtype.toLowerCase().includes('swppp');
  }

  saveUpdatedTimes(job: any, result: any): void {
    let totaltime = null;

    if (result.clockInTime && result.clockOutTime) {
      totaltime = this.calculateJobTotalTime(result.clockInTime, result.clockOutTime);
      if(totaltime.isNegative){
        this.messager.openSnackBar('Error: Start time cannot be AFTER end time.', '', 'errorAlert');
        return;
      }
    }

    const payload = {
      job_id: job.job_id,
      starttime: result.clockInTime || job.starttime,
      endtime: result.clockOutTime || job.endtime,
      timezone: this.givenData.day.timezone || "America/New_York",
      reason: result.reason,
      street_address: job.street_address,
      subdivision_name: job.subdivision_name,
      lot: job.lot,
      customer_jobtype: job.customer_jobtype,
      totaltime: totaltime,
      employee_id: this.givenData.employee_id,
      day: this.givenData.day,
      who: this.data.who
    };

    console.log('Saving updated times to API:', payload);

    this.scheduleService.updateJobTimes(payload).subscribe(
      (response) => {
        console.log('Times updated successfully:', response);
      },
      (error) => {
        console.error('Error updating times:', error);
      }
    );
  }

  convertTimeInputToISO(timeInput: string, originalISO: string): string {
    if (!timeInput || !originalISO) return originalISO;

    const [hours, minutes] = timeInput.split(':');
    const originalDate = new Date(originalISO);

    const newDate = new Date(originalDate);
    newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    return newDate.toISOString();
  }

  calculateJobTotalTime(startTime: string, endTime: string): { hours: number, minutes: number, seconds: number, isNegative?:boolean } {
    if (!startTime || !endTime) {
      return { hours: 0, minutes: 0, seconds: 0, isNegative: false };
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    const diffInMs = end.getTime() - start.getTime();
    if (diffInMs < 0) {
      return { hours: 0, minutes: 0, seconds: 0, isNegative: true };
    }

    const totalSeconds = Math.floor(diffInMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      hours: hours,
      minutes: minutes,
      seconds: seconds,
      isNegative: false
    };
  }

  convertToTimeZone(isoString: string): string {
    let timezone = this.givenData.day.timezone;
    let localTime = moment.utc(isoString).tz(timezone).format('YYYY-MM-DD HH:mm:ss');
    return localTime;
  }


  timeFunction(obj): string {

   if(obj)
   {
    let hours = obj.hours ? obj.hours.toString() : '00';
    let mins = obj.minutes ? obj.minutes.toString() : '00';
    let sec = obj.seconds ? obj.seconds.toString() : '00';
    return hours.padStart(2, '0') + ':' + mins.padStart(2, '0') + ':' + sec.padStart(2, '0');
   }else {
    return 'Still in progress. ';
   }
  }

  getLocalTime(date: string): string{
    return this.scheduleService.convertToLocalTime(date);
  }

  getTimeZoneDate(ds, includeDate) {
    if (!ds) {
      return 'N/A';
    }

    // Helper function to check if the date is in daylight saving time for US Eastern Time
    function isInDST(date) {
      const marchTransition = new Date(Date.UTC(date.getUTCFullYear(), 2, 14));
      const novemberTransition = new Date(Date.UTC(date.getUTCFullYear(), 10, 7));
      // Find the second Sunday in March
      marchTransition.setUTCDate(14 - (marchTransition.getUTCDay() || 7) + 7);
      // Find the first Sunday in November
      novemberTransition.setUTCDate(7 - (novemberTransition.getUTCDay() || 7));

      return date >= marchTransition && date < novemberTransition;
    }

    const date = new Date(ds);
    // Adjust for the standard -5 hours offset (Eastern Time)
    let utcOffsetHours = -5;
    // Check if the date falls in DST and adjust the offset to -4 if true
    if (isInDST(date)) {
      utcOffsetHours = -4;
    }

    date.setUTCHours(date.getUTCHours() + utcOffsetHours);

    // Format the date and time
    let hours = date.getUTCHours() % 12;
    hours = hours == 0 ? 12 : hours;
    const amOrpm = date.getUTCHours() >= 12 ? 'PM' : 'AM';

    const time = hours.toString().padStart(2, '0') + ":" +
                 date.getUTCMinutes().toString().padStart(2, '0') + ":" +
                 date.getUTCSeconds().toString().padStart(2, '0') + ' ' + amOrpm;

    if (includeDate) {
      const dateFormatted = (date.getUTCMonth() + 1).toString().padStart(2, '0') + '-' +
                            date.getUTCDate().toString().padStart(2, '0') + '-' +
                            date.getUTCFullYear();
      return dateFormatted + ' ' + time;
    } else {
      return time;
    }
  }

  intervalToString(obj: any): string {

      if (!obj) return '00h 00m';
      const hours = String(obj.hours ?? 0).padStart(2, '0');
      const minutes = String(obj.minutes ?? 0).padStart(2, '0');
      return `${hours}h ${minutes}m`;

  }

  calculateTravelTime(day): string {
        return this.intervalToString(day.traveltime);
  }

  calculateTotalTime(day): string {

      if( day.dayend === null){
        return 'Day still in progress';
      }

        const dayStart = new Date(this.data.day.daystart);
        const dayEnd = new Date(this.data.day.dayend);

        const pause = this.data.day.pausetime || {};
        const pausetimemills =
          (pause.hours ?? 0) * 3600000 +
          (pause.minutes ?? 0) * 60000 +
          (pause.seconds ?? 0) * 1000;
        const diffInMs = dayEnd.getTime() - dayStart.getTime() - pausetimemills;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));


        const totalMinutes = diffInMinutes % 60;
        const totalhours = Math.floor(diffInMinutes/ 60);

        return `${totalhours}h ${totalMinutes}m`;

  }


  calculatePaidTime(day): string {
      const SUBTRACTABLE_MINUTES = 30;

      if( day.dayend === null){
        return 'Day still in progress';
      }

        let finaltravelmins = 0;
        let finaltravelsecs = 0;

        const dayStart = new Date(this.data.day.daystart);
        const dayEnd = new Date(this.data.day.dayend);

        const pause = this.data.day.pausetime || {};
        const pausetimemills =
          (pause.hours ?? 0) * 3600000 +
          (pause.minutes ?? 0) * 60000 +
          (pause.seconds ?? 0) * 1000;
        const diffInMs = dayEnd.getTime() - dayStart.getTime() - pausetimemills;

        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if(!this.employeetype.toLowerCase().includes('office')){
          const traveltotimeSecs =
            (day.timetofirst?.hours ?? 0) * 3600 +
            (day.timetofirst?.minutes ?? 0) * 60 +
            (day.timetofirst?.seconds ?? 0) || 0;

          const travelhomeSecs =
            (day.timehome?.hours ?? 0) * 3600 +
            (day.timehome?.minutes ?? 0) * 60 +
            (day.timehome?.seconds ?? 0) || 0;

          //sub 30 here
          const subtractableseconds = SUBTRACTABLE_MINUTES* 60;
          let subtractable_travelhome = travelhomeSecs > subtractableseconds ? subtractableseconds: travelhomeSecs;
          let subtractable_tofirst = traveltotimeSecs > subtractableseconds ? subtractableseconds: traveltotimeSecs;


          if(subtractable_tofirst < 0) {
            subtractable_tofirst = 0;
          }

          if(subtractable_travelhome < 0) {
            subtractable_travelhome = 0;
          }

          finaltravelmins = Math.floor((subtractable_tofirst + subtractable_travelhome) / 60);
          finaltravelsecs = (subtractable_tofirst + subtractable_travelhome);

        }


        const paidMinutes = diffInMinutes - finaltravelmins;
        const paidSeconds = (diffInMs/ 1000) - (finaltravelsecs);
        const paidhours = Math.floor(paidMinutes / 60);
        const paidminutes = Math.floor(paidSeconds / 60) % 60;
        const paidsecs = paidSeconds % 60;

        return `${paidhours}h ${paidminutes}m ${paidsecs}s`;

  }


  totalTime(jobtime, traveltime ): string {

    let hours = 0, minutes = 0, jth = 0, jtm = 0, tth = 0, ttm = 0;

    console.log(this.employeetype)
    if(this.employeetype.toLowerCase().includes('office')){
      console.log('office');
      if( this.data.day.dayend === null){
        return 'Still in progress';
      }
        const dayStart = new Date(this.data.day.daystart);
        const dayEnd = new Date(this.data.day.dayend);

        const pause = this.data.day.pausetime || {};
        const pausetimemills =
          (pause.hours ?? 0) * 3600000 +
          (pause.minutes ?? 0) * 60000 +
          (pause.seconds ?? 0) * 1000;
        const diffInMs = dayEnd.getTime() - dayStart.getTime() - pausetimemills;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const ahours = Math.floor(diffInMinutes / 60);
        const aminutes = diffInMinutes % 60;
        return `${ahours}h ${aminutes}m`;
      }else {
      if(jobtime){
        if(jobtime.hours > 0){
          jth = jobtime.hours;
        }
        if(jobtime.minutes > 0){
          jtm = jobtime.minutes;
        }
      }
      if(traveltime){
        if(traveltime.hours > 0){
          tth = traveltime.hours;
        }
        if(traveltime.minutes > 0 ){
          ttm = traveltime.minutes;
        }
      }
      hours = jth + tth;
      minutes = jtm + ttm;
     if(minutes > 60) {
        hours++;
        minutes = minutes%60;
      }
    return hours + 'h :' + (minutes < 10 ? '0' + minutes + 'm' : minutes + 'm');
    }

 }



}

@Component({
  template: `
    <h2 mat-dialog-title style="color: #424242; font-family: Roboto; margin: 0; padding: 16px 24px; background: white;">
      {{ data.job ? data.job.subdivision_name + ' - ' + data.job.lot : 'General Timecard Note' }}
    </h2>
    <div mat-dialog-content style="padding: 0 24px; background: white;">
      <div *ngIf="notes.length > 0" style="margin-bottom: 15px; max-height: 200px; overflow-y: auto;">
        <div *ngFor="let n of notes" style="padding: 8px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: flex-start;">
          <span style="color: #424242; flex: 1; word-break: break-word; white-space: pre-wrap;">{{ n.note }}</span>
          <div style="display: flex; align-items: center; margin-left: 10px; white-space: nowrap;">
            <span *ngIf="n.created_by" style="color: #8a8a8a; font-size: 12px; margin-right: 8px;">{{ n.created_by }}</span>
            <span style="color: #8a8a8a; font-size: 12px; margin-right: 8px;">{{ n.created_at?.substring(0,10) }}</span>
            <i class="fa fa-trash" style="color: #910000; cursor: pointer;" (click)="deleteNote(n.id)" title="Delete note"></i>
          </div>
        </div>
      </div>
      <div *ngIf="notes.length === 0 && !isLoading" style="color: #8a8a8a; margin-bottom: 10px; font-style: italic;">
        No notes yet.
      </div>
      <div style="margin-bottom: 15px;">
        <textarea [(ngModel)]="noteText"
                  [disabled]="isLoading"
                  placeholder="Enter a note..."
                  rows="3"
                  style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; resize: vertical; font-family: Roboto; font-size: 14px; box-sizing: border-box;">
        </textarea>
      </div>
    </div>
    <div mat-dialog-actions style="justify-content: flex-end; padding: 8px 24px; background: white;">
      <button mat-button (click)="dialogRef.close()" style="color: #666;">Close</button>
      <button mat-raised-button (click)="saveNote()"
              [disabled]="!noteText?.trim() || isLoading"
              style="background-color: #477333; color: white; margin-left: 8px;">
        Save Note
      </button>
    </div>
  `
})
export class TimecardNoteDialogComponent implements OnInit {
  noteText: string = '';
  notes: any[] = [];
  isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<TimecardNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.loadNotes();
  }

  loadNotes(): void {
    if (!this.data.did) return;
    this.isLoading = true;
    this.data.scheduleService.getTimecardNotesByDay(this.data.did).subscribe(
      (res) => {
        const allNotes = res.Data || [];
        if (this.data.job_id) {
          this.notes = allNotes.filter(n => n.job_id != null && n.job_id == this.data.job_id);
        } else {
          this.notes = allNotes.filter(n => n.job_id == null);
        }
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        this.data.messager.openSnackBar('Error loading notes', '', 'errorAlert');
      }
    );
  }

  saveNote(): void {
    if (!this.noteText?.trim() || this.isLoading) return;

    this.isLoading = true;
    const payload = {
      did: this.data.did,
      note: this.noteText.trim(),
      employee_id: this.data.employee_id || 0,
      job_id: this.data.job_id || null,
      created_by: this.data.who || null,
      created_by_id: this.data.loggedInEmployeeId || null
    };

    this.data.scheduleService.createTimecardNote(payload).subscribe(
      (res) => {
        this.isLoading = false;
        this.noteText = '';
        this.data.messager.openSnackBar('Note saved', '', 'successAlert');
        this.loadNotes();
      },
      (error) => {
        this.isLoading = false;
        this.data.messager.openSnackBar('Error saving note', '', 'errorAlert');
      }
    );
  }

  deleteNote(noteId: number): void {
    this.data.scheduleService.deleteTimecardNote(noteId).subscribe(
      (res) => {
        this.data.messager.openSnackBar('Note deleted', '', 'successAlert');
        this.loadNotes();
      },
      (error) => {
        this.data.messager.openSnackBar('Error deleting note', '', 'errorAlert');
      }
    );
  }
}
